import axios, { AxiosError } from 'axios';
import axiosRetry, { IAxiosRetryConfig } from 'axios-retry';
import authProvider from './authProvider';
import truncate from 'lodash/truncate';
import { toast } from 'react-toastify';
import { setAuthHeader } from './apiHelpers';

// create an axios instance
const api = axios.create();

// DEFAULT HEADERS
api.defaults.headers.common['Accept'] = 'application/json';
api.defaults.headers.common['Content-Type'] = 'application/json';

// ADD RETRIES

// https://github.com/softonic/axios-retry/issues/87
const retryDelay = (retryNumber = 0) => {
    const seconds = Math.pow(2, retryNumber) * 1000;
    const randomMs = 1000 * Math.random();
    return seconds + randomMs;
};

axiosRetry(api, {
    retries: 2,
    retryDelay,

    // retry on Network Error & 5xx responses
    retryCondition: axiosRetry.isRetryableError
} as IAxiosRetryConfig);

// ADD AUTH
api.interceptors.request.use(async (config) => {
    const token = await authProvider.getToken();
    setAuthHeader(config.headers, token);
    return config;
});

// ERROR HANDLING
const configsWithErrorsShown: WeakMap<{}, boolean> = new WeakMap();

function showErrorMsg(errorMsg: string, config?: {} | nundefined) {
    // prevent multiple error messages when there are retries
    if (config && configsWithErrorsShown.has(config))
        return;

    toast.error(truncate(errorMsg, { length: 200 }), { autoClose: 10000 });
    
    if (config) {
        configsWithErrorsShown.set(config, true);
        setTimeout(() => configsWithErrorsShown.delete(config), 10000)
    }
}

api.interceptors.response.use(undefined, (error: AxiosError) => {
    const status = error?.response?.status ?? 0;
    
    if (status === 401) {
        authProvider.clearToken();
    }
    
    if (!skipErrorMessage(error)) {
        if (status === 401) {
            showErrorMsg('Error making request. Please try again.');
        } else {
            showErrorMsg(statusMsg(status) ?? 'Error making request', error?.config);
        }
    }
    
    return Promise.reject(error);
});

api.interceptors.request.use(undefined, error => {
    showErrorMsg(error?.message ?? 'Error making request', error?.config);
    return Promise.reject(error);
});

export default api;

function statusMsg(status: number): string | null {
    switch (status) {
        case 400:
            return 'Bad Request';
        case 401:
            return 'Unauthorized';
        case 403:
            return 'Forbidden, you do not have permission to perform this action.';
        case 404:
            return 'Not Found';
        case 405:
            return 'Method Not Allowed';
        case 406:
            return 'Not Acceptable';
        case 408:
            return 'Request Timeout';
        case 409:
            return 'Conflict';
        case 410:
            return 'Gone';
        case 413:
            return 'Request Entity Too Large';
        case 429:
            return 'Too Many Requests';
        case 500:
            return 'Internal Server Error';
        case 501:
            return 'Not Implemented';
        case 502:
            return 'Bad Gateway';
        case 503:
            return 'Service Unavailable';
        case 504:
            return 'Gateway Timeout';
        default:
            return status > 0 ? status.toString() : null;
    }
}

function skipErrorMessage(error: AxiosError): boolean {
    const path = error.config?.url ?? '';
    
    if (/\/engagements\/[^\/]+\/call-usage$/.test(path))
        return true;
    
    if (/\/calls\/[^\/]+\/audio$/.test(path))
        return true;
    
    if (/\/calls\/create-with-audio$/.test(path))
        return true;
    
    return false;
}
